import { aircraftType } from '../data/aircraft';
import { airportById } from '../data/airports';
import type { ActiveMarketEvent, AircraftAsset, Airport, CabinClass, CabinConfig, CabinProfile, CabinStats, GameState, HubAsset, Route, RouteForecast, RouteStats } from '../game/types';
import { airportCostMultiplier, applyAircraftMarketEventCosts, fuelCostMultiplier, routeDemandMultiplier, routeRevenueMultiplier } from './marketEvents';
import { routeReputationMultiplier } from './crew';
import { clamp, distanceKm, hashFloat } from './math';
import { emptyRouteStats } from '../game/createGame';

export const ROUTE_ADMIN_COST = 12_000;

/** 票价系统参数 */
const FARE_RATIO_MIN_EFFECTIVE = 1; // 0% 价格按 1% 计算，避免除以 0
const SHARE_ELASTICITY = 1.0; // 份额价格弹性
const OUTSIDE_ELASTICITY = 1.2; // 不坐飞机的外部选项弹性
const REPUTATION_PRICE_TOLERANCE = 0.8; // 口碑对高价的缓冲力度
const DEEP_DISCOUNT_RATIO = 30; // 低于此价格时，不再保留竞争者的最低基础份额
const ROUTE_MIN_MARKET_SHARE = 0.04; // 常规竞争中，每条运营航线保留少量基础客流
const OUTSIDE_BASE_UTILITY = 1.0; // 外部选项基准效用
export const FUEL_PRICE_BASE_PER_GALLON = 2.5;

export const CABIN_CLASSES: CabinClass[] = ['economy', 'business', 'first'];

export const CABIN_WEIGHTS: Record<CabinClass, number> = {
  economy: 1,
  business: 2,
  first: 3,
};

export function cabinWeightedSeats(config: CabinConfig): number {
  return config.economy * CABIN_WEIGHTS.economy + config.business * CABIN_WEIGHTS.business + config.first * CABIN_WEIGHTS.first;
}

export const CABIN_FARE_MULTIPLIER: Record<CabinClass, number> = {
  economy: 1,
  business: 3.5,
  first: 9,
};

export const EMPTY_CABIN_STATS: CabinStats = {
  seats: 0,
  demand: 0,
  passengers: 0,
  revenue: 0,
  loadFactor: 0,
  unmetDemand: 0,
};

export function fuelPriceIndex(week: number, seed: number): number {
  const cycle = Math.sin((week + seed % 19) / 8.5) * 0.12;
  const noise = (hashFloat(seed, week, 71) - 0.5) * 0.06;
  return clamp(1 + cycle + noise, 0.78, 1.28);
}

function distanceDemandFactor(distance: number): number {
  if (distance < 120) return 0.16;
  if (distance < 350) return 0.62;
  if (distance < 1_800) return 1;
  if (distance < 5_000) return 0.74;
  if (distance < 9_000) return 0.46;
  return 0.28;
}

function seasonalFactor(a: Airport, b: Airport, week: number): number {
  const tourismWeight = ((a.tourism + b.tourism) / 20) ** 1.25;
  const phase = ((a.lat + b.lat + 180) % 52) / 52 * Math.PI * 2;
  return 1 + Math.sin((week % 52) / 52 * Math.PI * 2 + phase) * 0.18 * tourismWeight;
}

type DemandState = Pick<GameState, 'week' | 'seed'> & { activeMarketEvents?: ActiveMarketEvent[] };

const routeKey = (a: string, b: string) => [a, b].sort().join('-');

export function aircraftServiceScore(asset: AircraftAsset, reputation: number): number {
  const crewMood = Number.isFinite(asset.crewMood) ? asset.crewMood : 60;
  return asset.condition * 0.30 + (crewMood / 100) * 0.30 + asset.crewSalaryModifier * 0.20 + (reputation / 100) * 0.20;
}

function aircraftForRoute(route: Route, state: GameState): AircraftAsset[] {
  if (state.aircraft.some((asset) => route.aircraftIds.includes(asset.id))) return state.aircraft;
  for (const manager of state.aiManagers ?? []) {
    if (manager.aircraft.some((asset) => route.aircraftIds.includes(asset.id))) return manager.aircraft;
  }
  return [];
}

function reputationForRoute(route: Route, state: GameState): number {
  if (state.aircraft.some((asset) => route.aircraftIds.includes(asset.id))) return state.reputation;
  for (const manager of state.aiManagers ?? []) {
    if (manager.aircraft.some((asset) => route.aircraftIds.includes(asset.id))) return manager.reputation;
  }
  return state.reputation;
}

export function aircraftFareRatio(asset: AircraftAsset, route?: Route): number {
  return Math.max(0, asset.fareRatio ?? route?.fareRatio ?? 100);
}

export function aircraftFareMultiplier(asset: AircraftAsset, route?: Route): number {
  return aircraftFareRatio(asset, route) / 100;
}

function routeAverageFareRatio(route: Route, aircraft: AircraftAsset[] = []): number {
  const assignedAircraft = route.aircraftIds
    .map((id) => aircraft.find((asset) => asset.id === id))
    .filter((asset): asset is AircraftAsset => asset !== undefined);
  if (!assignedAircraft.length) return route.fareRatio ?? 100;
  return assignedAircraft.reduce((sum, asset) => sum + aircraftFareRatio(asset, route), 0) / assignedAircraft.length;
}

function routeQualityScore(route: Route, aircraft: AircraftAsset[], reputation: number): number {
  const avgAircraftScore = route.aircraftIds.length
    ? route.aircraftIds
      .map((id) => aircraft.find((asset) => asset.id === id))
      .filter((asset): asset is AircraftAsset => asset !== undefined)
      .reduce((sum, asset) => sum + aircraftServiceScore(asset, reputation), 0) / route.aircraftIds.length
    : 0.5;
  return clamp((route.reputationScore ?? 25) / 100 * 0.65 + avgAircraftScore * 0.35, 0.02, 1);
}

function routeQualitySimilarity(route: Route, aircraft: AircraftAsset[], reputation: number, competitors: Route[], state?: GameState): number {
  if (competitors.length <= 1) return 1;
  const quality = routeQualityScore(route, aircraft, reputation);
  const nearestGap = Math.min(...competitors
    .filter((competitor) => competitor.id !== route.id)
    .map((competitor) => {
      const competitorAircraft = state ? aircraftForRoute(competitor, state) : aircraft;
      const competitorReputation = state ? reputationForRoute(competitor, state) : reputation;
      return Math.abs(quality - routeQualityScore(competitor, competitorAircraft, competitorReputation));
    }));
  return Math.exp(-nearestGap / 0.18);
}

export function routeEffectiveFareRatio(route: Route, aircraft: AircraftAsset[] = []): number {
  const ratio = Math.max(FARE_RATIO_MIN_EFFECTIVE, routeAverageFareRatio(route, aircraft));
  const reputation = route.reputationScore ?? 25;
  return ratio / (1 + (reputation / 25) * REPUTATION_PRICE_TOLERANCE);
}

export function routeFareMultiplier(route: Route): number {
  return (route.fareRatio ?? 100) / 100;
}

export function routeAttractiveness(route: Route, aircraft: AircraftAsset[], reputation: number, competitors: Route[] = [], state?: GameState): number {
  const seats = route.stats?.seats ?? 0;
  const avgAircraftScore = route.aircraftIds.length
    ? route.aircraftIds
      .map((id) => aircraft.find((asset) => asset.id === id))
      .filter((asset): asset is AircraftAsset => asset !== undefined)
      .reduce((sum, asset) => sum + aircraftServiceScore(asset, reputation), 0) / route.aircraftIds.length
    : 0.5;
  const serviceFactor = (routeReputationMultiplier(route.reputationScore ?? 25) + avgAircraftScore) / 2;
  const quality = routeQualityScore(route, aircraft, reputation);
  const similarity = routeQualitySimilarity(route, aircraft, reputation, competitors, state);
  const effectiveFare = routeEffectiveFareRatio(route, aircraft);
  const origin = airportById(route.originId);
  const destination = airportById(route.destinationId);
  const profile = routeCabinProfile(origin, destination);
  return CABIN_CLASSES.reduce((total, cabin) => {
    const cabinSeats = route.stats?.cabins?.[cabin]?.seats ?? seats * profile[cabin];
    const priceElasticity = cabin === 'first' ? 0 : cabin === 'business' ? 0.12 + similarity * 0.55 : 0.85 + similarity * 0.35;
    const qualityFactor = cabin === 'first'
      ? 0.08 + quality ** 1.7 * 1.02
      : cabin === 'business'
        ? 0.38 + quality * 0.72
        : 0.70 + quality * 0.30;
    const fareFactor = priceElasticity === 0 ? 1 : (100 / effectiveFare) ** priceElasticity;
    return total + cabinSeats * serviceFactor * qualityFactor * fareFactor;
  }, 0);
}

export function routeCabinDemandMultiplier(route: Route, cabin: CabinClass, state: GameState, manager?: { reputation: number; aircraft: AircraftAsset[] }): number {
  const aircraft = manager?.aircraft ?? aircraftForRoute(route, state);
  const reputation = manager?.reputation ?? reputationForRoute(route, state);
  const quality = routeQualityScore(route, aircraft, reputation);
  if (cabin === 'first') return clamp(0.08 + quality ** 1.7 * 1.02, 0.08, 1.05);
  if (cabin === 'business') return clamp(0.38 + quality * 0.72, 0.38, 1.10);
  return 1;
}

export function routesOnCityPair(originId: string, destinationId: string, state: GameState): Route[] {
  const key = routeKey(originId, destinationId);
  const playerRoutes = state.routes.filter((route) => route.active && routeKey(route.originId, route.destinationId) === key);
  const aiRoutes = (state.aiManagers ?? [])
    .filter((manager) => !manager.bankrupt)
    .flatMap((manager) => manager.routes.filter((route) => route.active && routeKey(route.originId, route.destinationId) === key));
  return [...playerRoutes, ...aiRoutes];
}

function marketEffectiveAverageFareRatio(routes: Route[], state: GameState): number {
  const totalSeats = routes.reduce((sum, route) => sum + (route.stats?.seats ?? 0), 0);
  if (totalSeats <= 0) return 100;
  return routes.reduce((sum, route) => sum + routeEffectiveFareRatio(route, aircraftForRoute(route, state)) * (route.stats?.seats ?? 0), 0) / totalSeats;
}

function outsideOptionAttractiveness(routes: Route[], baseDemand: number, state: GameState): number {
  const avg = marketEffectiveAverageFareRatio(routes, state);
  const utility = OUTSIDE_BASE_UTILITY * (Math.max(1, avg / 100) ** OUTSIDE_ELASTICITY);
  return utility * baseDemand;
}

function computeMarketShare(route: Route, competitors: Route[], state: GameState, manager?: { reputation: number; aircraft: AircraftAsset[] }): number {
  const routeAttractivenessTotal = competitors.reduce((sum, competitor) => {
    const aircraft = aircraftForRoute(competitor, state);
    const reputation = reputationForRoute(competitor, state);
    return sum + routeAttractiveness(competitor, aircraft, reputation, competitors, state);
  }, 0);
  const total = routeAttractivenessTotal + outsideOptionAttractiveness(competitors, marketDemand(airportById(route.originId), airportById(route.destinationId), state), state);
  if (total <= 0) return 1;
  const rawRouteShares = competitors.map((competitor) => {
    const aircraft = competitor.id === route.id
      ? manager?.aircraft ?? aircraftForRoute(competitor, state)
      : aircraftForRoute(competitor, state);
    const reputation = competitor.id === route.id
      ? manager?.reputation ?? reputationForRoute(competitor, state)
      : reputationForRoute(competitor, state);
    return {
      routeId: competitor.id,
      share: routeAttractiveness(competitor, aircraft, reputation, competitors, state) / total,
    };
  });
  const rawShare = rawRouteShares.find((item) => item.routeId === route.id)?.share ?? 0;
  const deepDiscountActive = competitors.some((item) => routeAverageFareRatio(item, aircraftForRoute(item, state)) < DEEP_DISCOUNT_RATIO);
  if (deepDiscountActive || competitors.length <= 1) return rawShare;

  const outsideShare = Math.max(0, 1 - (routeAttractivenessTotal / total));
  const routePool = Math.max(0, 1 - outsideShare);
  const floorBudget = Math.min(routePool * 0.35, ROUTE_MIN_MARKET_SHARE * competitors.length);
  const minShare = floorBudget / competitors.length;
  const lowShareCount = rawRouteShares.filter((item) => item.share < minShare).length;
  const guaranteedShare = lowShareCount * minShare;
  const flexibleShare = Math.max(0, routePool - guaranteedShare);
  const highRawTotal = rawRouteShares
    .filter((item) => item.share >= minShare)
    .reduce((sum, item) => sum + item.share, 0);
  if (rawShare < minShare) return minShare;
  if (highRawTotal <= 0) return rawShare;
  return rawShare / highRawTotal * flexibleShare;
}

export function routeMarketShare(route: Route, state: GameState): number {
  const competitors = routesOnCityPair(route.originId, route.destinationId, state);
  return computeMarketShare(route, competitors, state);
}

export function aiRouteMarketShare(route: Route, manager: { reputation: number; aircraft: AircraftAsset[] }, state: GameState): number {
  const competitors = routesOnCityPair(route.originId, route.destinationId, state);
  return computeMarketShare(route, competitors, state, manager);
}

export function marketDemand(a: Airport, b: Airport, state: DemandState): number {
  const distance = distanceKm(a, b);
  const mass = 80 * (a.market * b.market) ** 1.05;
  const businessLink = 0.78 + ((a.business + b.business) / 20) * 0.33;
  const tourismLink = 0.82 + ((a.tourism + b.tourism) / 20) * 0.26;
  const international = a.region === b.region ? 1 : 0.82 + (a.international + b.international) / 100;
  const marketNoise = 0.94 + hashFloat(state.seed, state.week, a.iata.charCodeAt(0), b.iata.charCodeAt(0)) * 0.12;

  return Math.max(0, Math.round(
    mass * distanceDemandFactor(distance) * businessLink * tourismLink * international
    * seasonalFactor(a, b, state.week) * marketNoise,
  ));
}

export function baseFare(distance: number, cabin: CabinClass): number {
  const distanceFare = 42 + distance * (distance < 1_500 ? 0.105 : distance < 6_000 ? 0.085 : 0.07);
  return distanceFare * CABIN_FARE_MULTIPLIER[cabin];
}

export function routeCabinProfile(origin: Airport, destination: Airport): CabinProfile {
  const raw = {
    economy: Math.sqrt(origin.cabinProfile.economy * destination.cabinProfile.economy),
    business: Math.sqrt(origin.cabinProfile.business * destination.cabinProfile.business),
    first: Math.sqrt(origin.cabinProfile.first * destination.cabinProfile.first),
  };
  const total = raw.economy + raw.business + raw.first;
  return {
    economy: raw.economy / total,
    business: raw.business / total,
    first: raw.first / total,
  };
}

export function routeLaunchCost(origin: Airport, destination: Airport): number {
  const distance = distanceKm(origin, destination);
  return Math.round(250_000 + distance * 360 + (origin.feeIndex + destination.feeIndex) * 95_000);
}

export function calculateRouteCabins(
  distance: number,
  baseDemand: number,
  cabinProfile: CabinProfile,
  cabinSeats: Record<CabinClass, number>,
  fareMultiplier: number,
  cabinDemandMultipliers: Partial<Record<CabinClass, number>> = {},
): { cabins: Record<CabinClass, CabinStats>; demand: number; passengers: number; revenue: number } {
  const cabins = {} as Record<CabinClass, CabinStats>;
  let demand = 0;
  let passengers = 0;
  let revenue = 0;

  for (const cabin of CABIN_CLASSES) {
    const cabinDemand = Math.round(baseDemand * cabinProfile[cabin] * (cabinDemandMultipliers[cabin] ?? 1));
    const cabinSeatCount = cabinSeats[cabin];
    const cabinPassengers = Math.min(cabinDemand, cabinSeatCount);
    const cabinRevenue = cabinPassengers * baseFare(distance, cabin) * fareMultiplier;
    demand += cabinDemand;
    passengers += cabinPassengers;
    revenue += cabinRevenue;
    cabins[cabin] = {
      seats: cabinSeatCount,
      demand: cabinDemand,
      passengers: cabinPassengers,
      revenue: Math.round(cabinRevenue),
      loadFactor: cabinSeatCount > 0 ? Math.min(1, cabinPassengers / cabinSeatCount) : 0,
      unmetDemand: Math.max(0, cabinDemand - cabinPassengers),
    };
  }

  return { cabins, demand, passengers, revenue };
}

export function hubBuildCost(airport: Airport): number {
  return 5_000_000 + airport.market * 1_150_000 + airport.international * 250_000;
}

export function hubWeeklyCost(airport: Airport, level = 1): number {
  return hubWeeklyCostForSpecialization(airport, level, 'balanced');
}

export const HUB_MAX_LEVEL = 4;

export const HUB_SPECIALIZATION_LABELS: Record<HubAsset['specialization'], string> = {
  balanced: '均衡型',
  connection: '中转枢纽',
  lowcost: '廉航枢纽',
  premium: '高端枢纽',
};

export function hubSpecializationEffects(specialization: HubAsset['specialization']) {
  switch (specialization) {
    case 'connection':
      return { demand: 1.04, revenue: 1, airportCost: 1.04, passengerCost: 1, transfer: 1.45, weeklyCost: 1.18 };
    case 'lowcost':
      return { demand: 1.06, revenue: 0.96, airportCost: 0.86, passengerCost: 0.9, transfer: 0.92, weeklyCost: 1.08 };
    case 'premium':
      return { demand: 0.98, revenue: 1.11, airportCost: 1.08, passengerCost: 1.08, transfer: 0.95, weeklyCost: 1.32 };
    case 'balanced':
    default:
      return { demand: 1.02, revenue: 1.02, airportCost: 0.98, passengerCost: 0.98, transfer: 1.08, weeklyCost: 1 };
  }
}

export function hubWeeklyCostForSpecialization(airport: Airport, level = 1, specialization: HubAsset['specialization'] = 'balanced'): number {
  const effects = hubSpecializationEffects(specialization);
  return Math.round((38_000 + airport.market * 4_200) * (1 + (level - 1) * 0.55) * effects.weeklyCost);
}

export function hubUpgradeCost(airport: Airport, currentLevel: number): number {
  const nextLevel = currentLevel + 1;
  return Math.round(hubBuildCost(airport) * (0.48 + nextLevel * 0.34));
}

function hubForRoute(route: Route, state: GameState, manager?: { hubs?: HubAsset[] }): HubAsset | undefined {
  const hubs = manager?.hubs ?? state.hubs;
  return hubs.find((hub) => hub.airportId === route.originId)
    ?? hubs.find((hub) => hub.airportId === route.destinationId);
}

function hubRouteMultiplier(hub?: HubAsset) {
  if (!hub) return { demand: 1, revenue: 1, airportCost: 1, passengerCost: 1 };
  const effects = hubSpecializationEffects(hub.specialization);
  const levelBonus = 1 + (hub.level - 1) * 0.035;
  return {
    demand: effects.demand * levelBonus,
    revenue: effects.revenue * (1 + (hub.level - 1) * 0.018),
    airportCost: effects.airportCost,
    passengerCost: effects.passengerCost,
  };
}

// 新手保护期：前 28 周内开通的航线培育起步系数从 0.55 提升到 0.80。
export const EARLY_GAME_MATURITY_WEEKS = 28;

export function maturityFactor(maturityWeeks: number, openedWeek?: number): number {
  const floor = openedWeek !== undefined && openedWeek <= EARLY_GAME_MATURITY_WEEKS ? 0.8 : 0.55;
  return clamp(floor + maturityWeeks * 0.045, floor, 1);
}

// 枢纽费新手减免：前 20 周收 20%，21-30 周收 30%，31-40 周收 50%，41-50 周收 70%，之后恢复 100%。
export function hubFeeDiscountFactor(week: number): number {
  if (week <= 20) return 0.2;
  if (week <= 30) return 0.3;
  if (week <= 40) return 0.5;
  if (week <= 50) return 0.7;
  return 1;
}

export interface AircraftOperation {
  roundTrips: number;
  seats: number;
  cabins: Record<CabinClass, number>;
  flightHours: number;
  fuelGallons: number;
  ownershipCost: number;
  cycleCost: number;
  fixedCost: number;
  flightCost: number;
}

export function aircraftOperation(asset: AircraftAsset, distance: number, fuelIndex: number, speedModifier = asset.speedModifier ?? 1): AircraftOperation {
  const type = aircraftType(asset.typeId);
  const cabinConfig = asset.cabinConfig ?? type.cabins;
  const conditionEfficiency = clamp(0.86 + asset.condition * 0.14, 0.90, 1);
  const shortStagePenalty = 1 + clamp((type.idealStageKm - distance) / type.idealStageKm, 0, 1) * 0.35;
  const effectiveSpeed = type.speedKmh * speedModifier;
  const oneWayBlockHours = distance / effectiveSpeed + type.turnaroundHours * shortStagePenalty;
  const cycleHours = oneWayBlockHours * 2;
  const roundTrips = Math.max(1, Math.min(32, Math.floor((type.weeklyHours * conditionEfficiency) / cycleHours)));
  const flightHours = roundTrips * (2 * distance) / effectiveSpeed;
  const conditionWear = Math.max(0, 1 - asset.condition);
  const baseOwnershipCost = asset.lease ? asset.lease.weeklyPayment : type.weeklyFixedCost;
  const ownershipCost = baseOwnershipCost * (1 + conditionWear * (asset.lease ? 0.18 : 0.45));
  const cycleCost = roundTrips * 2 * type.cycleCost * (1 + conditionWear * 1.2);
  const speedFuelExponent = 2;
  const fuelGallonsPerHour = type.fuelCostPerHour / FUEL_PRICE_BASE_PER_GALLON;
  const fuelGallons = flightHours * fuelGallonsPerHour * (speedModifier ** speedFuelExponent) * (1 + conditionWear * 0.12);
  const flightCost = fuelGallons * FUEL_PRICE_BASE_PER_GALLON * fuelIndex;

  const cabins: Record<CabinClass, number> = {
    economy: roundTrips * cabinConfig.economy * 2,
    business: roundTrips * cabinConfig.business * 2,
    first: roundTrips * cabinConfig.first * 2,
  };

  return {
    roundTrips,
    seats: roundTrips * (cabinConfig.economy + cabinConfig.business + cabinConfig.first) * 2,
    cabins,
    flightHours,
    fuelGallons,
    ownershipCost,
    cycleCost,
    fixedCost: ownershipCost + cycleCost,
    flightCost,
  };
}

export function forecastRoute(
  state: GameState,
  originId: string,
  destinationId: string,
  aircraft: AircraftAsset,
  _legacyStrategy?: unknown,
  manager?: { reputation: number; aircraft: AircraftAsset[]; hubs?: HubAsset[] },
): RouteForecast {
  const origin = airportById(originId);
  const destination = airportById(destinationId);
  const type = aircraftType(aircraft.typeId);
  const distance = distanceKm(origin, destination);
  const launchCost = routeLaunchCost(origin, destination);
  const rangeCompatible = distance <= type.rangeKm;
  const runwayCompatible = type.runwayClass <= origin.runwayClass && type.runwayClass <= destination.runwayClass;
  const operation = applyAircraftMarketEventCosts(aircraft, aircraftOperation(aircraft, distance, fuelPriceIndex(state.week + 1, state.seed) * fuelCostMultiplier(state)), state);
  const forecastStats = emptyRouteStats(distance);
  forecastStats.seats = operation.seats;
  for (const cabin of CABIN_CLASSES) {
    forecastStats.cabins[cabin] = {
      ...forecastStats.cabins[cabin],
      seats: operation.cabins[cabin],
    };
  }
  const tempRoute: Route = {
    id: 'forecast-temp',
    originId,
    destinationId,
    aircraftIds: [aircraft.id],
    openedWeek: state.week,
    maturityWeeks: 0,
    launchInvestment: 0,
    active: true,
    reputationScore: 25,
    fareRatio: 100,
    stats: forecastStats,
  };
  const forecastState = { ...state, routes: [...state.routes, tempRoute] };
  const marketShare = manager
    ? aiRouteMarketShare(tempRoute, manager, forecastState)
    : routeMarketShare(tempRoute, forecastState);
  const tempHub = hubForRoute(tempRoute, state, manager);
  const hubMultiplier = hubRouteMultiplier(tempHub);
  const baseDemand = Math.round(marketDemand(origin, destination, { ...state, week: state.week + 1 }) * marketShare * routeDemandMultiplier(undefined, origin, destination, state) * hubMultiplier.demand);
  const profile = routeCabinProfile(origin, destination);
  const fareMultiplier = routeRevenueMultiplier(undefined, origin, destination, state) * aircraftFareMultiplier(aircraft, tempRoute) * hubMultiplier.revenue;
  const cabinDemandMultipliers = Object.fromEntries(CABIN_CLASSES.map((cabin) => [cabin, routeCabinDemandMultiplier(tempRoute, cabin, forecastState, manager)])) as Partial<Record<CabinClass, number>>;
  const { cabins, demand, passengers, revenue } = calculateRouteCabins(distance, baseDemand, profile, operation.cabins, fareMultiplier, cabinDemandMultipliers);
  const landingCost = operation.roundTrips * (800 + (origin.feeIndex + destination.feeIndex) * 900) * airportCostMultiplier(origin.iata, destination.iata, state) * hubMultiplier.airportCost;
  const passengerCost = passengers * type.perPassengerCost * aircraft.crewSalaryModifier * hubMultiplier.passengerCost;
  const cashCost = operation.fixedCost + operation.flightCost + landingCost + passengerCost + ROUTE_ADMIN_COST;
  const weeklyProfit = revenue - cashCost;
  // 回本口径包含航线投入与占用的飞机资本，避免只计算申请费而产生“数周回本”的假象。
  const capitalEmployed = launchCost + aircraft.bookValue;
  const paybackWeeks = weeklyProfit > 0 ? Math.ceil(capitalEmployed / weeklyProfit) : null;
  const reasons: string[] = [];

  if (origin.business + destination.business >= 15) reasons.push('两地商务联系带来稳定基础需求');
  if (origin.market + destination.market >= 18) reasons.push('热门大市场竞争较强，需关注价格与服务定位');
  if (origin.tourism + destination.tourism >= 15) reasons.push('旅游属性较强，旺季上行空间明显');
  if (distance < 120) reasons.push('距离过近，地面交通会显著分流');
  if (operation.seats > demand * 1.45) reasons.push('当前机型运力偏大，存在较多空座');
  if (operation.seats < demand * 0.72) reasons.push('需求明显高于单机运力，后续可追加飞机');
  if (origin.feeIndex + destination.feeIndex > 3) reasons.push('两端机场费用较高');
  if (paybackWeeks && paybackWeeks > 260) reasons.push('包含飞机资本后的回本周期超过5年');
  if (!rangeCompatible) reasons.push('机型航程不足');
  if (!runwayCompatible) reasons.push('机场跑道等级不支持该机型');

  return {
    distanceKm: distance,
    launchCost,
    seats: operation.seats,
    demand,
    passengers,
    loadFactor: operation.seats > 0 ? Math.min(1, passengers / operation.seats) : 0,
    revenue,
    cashCost,
    weeklyProfit,
    paybackWeeks,
    rangeCompatible,
    runwayCompatible,
    cashAfterLaunch: state.cash - launchCost,
    reasons,
    cabins,
  };
}

export function estimateRouteStats(state: GameState, route: Route, manager?: { reputation: number; aircraft: AircraftAsset[]; hubs?: HubAsset[] }): RouteStats {
  const origin = airportById(route.originId);
  const destination = airportById(route.destinationId);
  const distance = distanceKm(origin, destination);
  const fuelIndex = fuelPriceIndex(state.week, state.seed) * fuelCostMultiplier(state);
  const aircraftSource = manager?.aircraft ?? state.aircraft;
  const operations = route.aircraftIds
    .map((aircraftId) => aircraftSource.find((item) => item.id === aircraftId))
    .filter((asset) => asset !== undefined)
    .map((asset) => ({
      asset,
      operation: applyAircraftMarketEventCosts(asset, aircraftOperation(asset, distance, fuelIndex), state),
    }));
  const roundTrips = operations.reduce((sum, item) => sum + item.operation.roundTrips, 0);
  const seats = operations.reduce((sum, item) => sum + item.operation.seats, 0);
  const flightCost = operations.reduce((sum, item) => sum + item.operation.flightCost, 0);
  const fixedAircraftCost = operations.reduce((sum, item) => sum + item.operation.fixedCost, 0);
  const hub = hubForRoute(route, state, manager);
  const hubMultiplier = hubRouteMultiplier(hub);
  const routeAircraft = operations.map((item) => item.asset);
  const averageFareMultiplier = routeAircraft.length
    ? routeAircraft.reduce((sum, asset) => sum + aircraftFareMultiplier(asset, route), 0) / routeAircraft.length
    : routeFareMultiplier(route);
  const fareMultiplier = routeRevenueMultiplier(route, origin, destination, state) * averageFareMultiplier * hubMultiplier.revenue;
  const profile = routeCabinProfile(origin, destination);

  const cabinSeats: Record<CabinClass, number> = { economy: 0, business: 0, first: 0 };
  for (const item of operations) {
    for (const cabin of CABIN_CLASSES) {
      cabinSeats[cabin] += item.operation.cabins[cabin];
    }
  }

  const marketRoute: Route = {
    ...route,
    stats: {
      ...route.stats,
      seats,
      cabins: Object.fromEntries(CABIN_CLASSES.map((cabin) => [cabin, { ...route.stats.cabins[cabin], seats: cabinSeats[cabin] }])) as RouteStats['cabins'],
    },
  };
  const forecastState = state.routes.some((item) => item.id === route.id)
    ? { ...state, routes: state.routes.map((item) => item.id === route.id ? marketRoute : item) }
    : state;
  const marketShare = manager ? aiRouteMarketShare(marketRoute, manager, forecastState) : routeMarketShare(marketRoute, forecastState);
  const baseDemand = marketDemand(origin, destination, state) * marketShare * maturityFactor(route.maturityWeeks, route.openedWeek) * routeDemandMultiplier(route, origin, destination, state) * hubMultiplier.demand;

  const cabinDemandMultipliers = Object.fromEntries(CABIN_CLASSES.map((cabin) => [cabin, routeCabinDemandMultiplier(route, cabin, state, manager)])) as Partial<Record<CabinClass, number>>;
  const { cabins, demand, passengers, revenue } = calculateRouteCabins(distance, baseDemand, profile, cabinSeats, fareMultiplier, cabinDemandMultipliers);

  const landingCost = roundTrips * (800 + (origin.feeIndex + destination.feeIndex) * 900) * airportCostMultiplier(origin.iata, destination.iata, state) * hubMultiplier.airportCost;
  const passengerCost = operations.reduce((sum, item) => sum + aircraftType(item.asset.typeId).perPassengerCost * item.asset.crewSalaryModifier, 0)
    / Math.max(1, operations.length) * passengers * hubMultiplier.passengerCost;
  const cashCost = flightCost + fixedAircraftCost + landingCost + passengerCost + ROUTE_ADMIN_COST;

  return {
    distanceKm: distance,
    roundTrips,
    seats,
    demand,
    passengers,
    localPassengers: passengers,
    transferPassengers: 0,
    loadFactor: seats > 0 ? Math.min(1, passengers / seats) : 0,
    unmetDemand: Math.max(0, demand - passengers),
    revenue,
    cashCost,
    operatingProfit: revenue - cashCost,
    networkContribution: 0,
    cabins,
    costs: {
      fuel: flightCost,
      ownership: fixedAircraftCost,
      cycle: 0,
      landing: landingCost,
      passenger: passengerCost,
      admin: ROUTE_ADMIN_COST,
    },
  };
}
